
import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/context/AuthContext";
import { TokenStatus, useInvitationToken } from "@/hooks/useInvitationToken";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { sendEmailVerificationWithContinueUrl } from "@/services/authVerification";
import { ensureInviteClaimsReady, persistInviteToAccountWithToken } from "@/auth/inviteAuth";
import { acceptInvitation } from "@/services/userInvitationService";
import { ensureUserIdentityBaseline, IdentityBaselineSource } from "@/services/userIdentityBaselineService";
import { mergeUserAllowedFieldsAfterSignup } from "@/services/userProfileAllowedWrites";
import { toast } from "sonner";
import { useFirestoreError } from "@/hooks/useFirestoreError";
import {
  passwordSchema,
  PasswordFormData,
  LoadingState,
  InvalidTokenState,
  ExpiredTokenState,
  AlreadyAcceptedState,
  ValidTokenState,
  CreatingAccountState,
  AccountCreatedState
} from "./WelcomeStates";
import { LocationEditingState } from "./WelcomeLocationEditingState";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { WelcomeState } from "./WelcomeDebugInfo";

interface WelcomeControllerProps {
  tokenValue: string | null;
}

// grantInviteClaims' own refusals (functions/src/invitations/grantInviteClaims.ts). Only these
// mean the invitation itself is bad; any other failure says nothing about the link.
const INVITE_REFUSAL_CODES = new Set([
  "functions/not-found",
  "functions/permission-denied",
  "functions/failed-precondition",
  "functions/invalid-argument",
]);

function isInviteRefusal(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code;
  return typeof code === "string" && INVITE_REFUSAL_CODES.has(code);
}

const InviteClaimUnavailableState: React.FC = () => (
  <Card className="w-full max-w-md mx-auto">
    <CardHeader>
      <h2 className="text-xl font-semibold text-center">We couldn't open your invitation</h2>
    </CardHeader>
    <CardContent>
      <Alert>
        <AlertDescription>
          Something went wrong while opening your invitation. Your link may be fine.
          <br /><br />Please reload this page to try again. If it keeps happening, contact the
          person who sent you the invitation.
        </AlertDescription>
      </Alert>
    </CardContent>
  </Card>
);

const WelcomeController: React.FC<WelcomeControllerProps> = ({ tokenValue }) => {
  const [welcomeState, setWelcomeState] = useState<WelcomeState>(WelcomeState.LOADING);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentUser } = useAuth();
  const { handleFirestoreError } = useFirestoreError();
  
  // Use our custom hook for invitation token handling
  const { tokenStatus, invitation, hasUpdatedViewedTime } = useInvitationToken(tokenValue);

  // KB-3969: a location-edit invitee must hold its invite claims before LocationEditingState
  // mounts, because that screen lists campLocations / campOrganizations with no limit and
  // kb-ff's rules admit that only to a staff role claim. The ref keeps the anonymous sign-in
  // (which changes currentUser and re-runs the effect below) from starting a second claim.
  // "refused" = the server said no (bad, revoked or broken invitation), so the invalid-invitation
  // screen is true. "unavailable" = anything else (network, a server error, claims not on the
  // token yet): the link may be fine, so the page must not call it invalid.
  const [inviteClaim, setInviteClaim] = useState<"idle" | "pending" | "ready" | "refused" | "unavailable">("idle");
  const claimStartedForToken = useRef<string | null>(null);
  
  const form = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
    },
  });
  
  // Update welcome state based on token status and user authentication
  useEffect(() => {
    console.log("Token status updated:", TokenStatus[tokenStatus], "Invitation:", invitation);
    if (invitation) {
      console.log("Invitation type:", invitation.type || "user_invite (default)", "Invitation status:", invitation.status);
    }
    
    // Handle case where user is authenticated but has no token
    if (!tokenValue && currentUser && !currentUser.isAnonymous) {
      console.log("No token but user is authenticated - showing location editing interface");
      setWelcomeState(WelcomeState.EDITING_LOCATION);
      return;
    }
    
    // Handle case where anonymous user has no token but should have guest access via custom claims
    if (!tokenValue && currentUser && currentUser.isAnonymous) {
      console.log("No token but user is anonymous - checking for guest access via custom claims");
      currentUser.getIdTokenResult().then(tokenResult => {
        const claimsRole = tokenResult.claims.role;
        const claimsOrgId = tokenResult.claims.organizationId;
        
        if (claimsRole && claimsOrgId) {
          console.log("Anonymous user has guest access via custom claims - showing location editing interface");
          setWelcomeState(WelcomeState.EDITING_LOCATION);
        } else {
          console.log("Anonymous user without valid claims - showing invalid token");
          setWelcomeState(WelcomeState.INVALID_TOKEN);
        }
      }).catch(error => {
        console.error("Error checking custom claims:", error);
        setWelcomeState(WelcomeState.INVALID_TOKEN);
      });
      return;
    }
    
    switch (tokenStatus) {
      case TokenStatus.LOADING:
        setWelcomeState(WelcomeState.LOADING);
        break;
        
      case TokenStatus.INVALID_TOKEN:
        setWelcomeState(WelcomeState.INVALID_TOKEN);
        break;
        
      case TokenStatus.EXPIRED_TOKEN:
        setWelcomeState(WelcomeState.EXPIRED_TOKEN);
        break;
        
      case TokenStatus.ALREADY_ACCEPTED:
        setWelcomeState(WelcomeState.ALREADY_ACCEPTED);
        break;
        
      case TokenStatus.VALID_TOKEN:
        // Determine state based on invitation type
        if (invitation && invitation.type === "location_edit") {
          if (inviteClaim === "ready") {
            console.log("Setting welcome state to EDITING_LOCATION - invite claims are on the token");
            setWelcomeState(WelcomeState.EDITING_LOCATION);
          } else if (inviteClaim === "refused") {
            setWelcomeState(WelcomeState.INVALID_TOKEN);
          } else if (inviteClaim !== "unavailable") {
            setWelcomeState(WelcomeState.LOADING);
            if (tokenValue && claimStartedForToken.current !== tokenValue) {
              claimStartedForToken.current = tokenValue;
              setInviteClaim("pending");
              ensureInviteClaimsReady(tokenValue)
                .then(() => setInviteClaim("ready"))
                .catch((error) => {
                  console.error("Could not claim the location-edit invitation:", error);
                  setInviteClaim(isInviteRefusal(error) ? "refused" : "unavailable");
                });
            }
          }
        } else {
          console.log("Setting welcome state to VALID_TOKEN for account creation");
          setWelcomeState(WelcomeState.VALID_TOKEN);
        }
        break;
    }
  }, [tokenStatus, invitation, tokenValue, currentUser, inviteClaim]);
  
  const handleCreateAccount = async (data: PasswordFormData) => {
    if (!invitation) {
      return;
    }
    
    setIsSubmitting(true);
    setWelcomeState(WelcomeState.CREATING_ACCOUNT);
    
    try {
      // Create new Firebase auth user
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password
      );
      
      const user = userCredential.user;
      
      // Send email verification with environment-aware continueUrl
      await sendEmailVerificationWithContinueUrl(user);
      
      // Server-side: role, organizationId, claims (Admin SDK) — client cannot set these on users/{uid}
      if (tokenValue) {
        await persistInviteToAccountWithToken(tokenValue);
      }

      // Workaround for the documented setCustomUserClaims -> fresh-token race:
      // the first refresh sometimes returns a token without the just-minted
      // claims. A short delay + second refresh reliably picks them up before
      // the SPA navigates to any protected operation. See KB-136.
      await user.getIdToken(true);
      await new Promise((r) => setTimeout(r, 750));
      await user.getIdToken(true);

      await ensureUserIdentityBaseline({
        source: IdentityBaselineSource.WELCOME_INVITE_PASSWORD
      });

      // Mark invitation as accepted (Firestore rules on invitation doc)
      if (tokenValue) {
        await acceptInvitation(tokenValue, user.uid);
      }

      await mergeUserAllowedFieldsAfterSignup({
        uid: user.uid,
        firstName: data.firstName,
        lastName: data.lastName,
        lastLogin: new Date()
      });
      
      setWelcomeState(WelcomeState.ACCOUNT_CREATED);
      toast.success(
        <div>
          <div>Account created successfully.</div>
          <div className="text-sm">
            You can also log in to kidsbooked.com with the same account.
          </div>
        </div>
      );
    } catch (error) {
      console.error("Error creating account:", error);
      toast.error("Failed to create account");
      setWelcomeState(WelcomeState.VALID_TOKEN);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render the appropriate component based on the welcome state
  const renderContent = () => {
    console.log("Rendering content for welcome state:", WelcomeState[welcomeState]);

    if (inviteClaim === "unavailable") {
      return <InviteClaimUnavailableState />;
    }

    switch (welcomeState) {
      case WelcomeState.LOADING:
        return <LoadingState />;
        
      case WelcomeState.INVALID_TOKEN:
        return <InvalidTokenState />;
        
      case WelcomeState.EXPIRED_TOKEN:
        return <ExpiredTokenState />;
        
      case WelcomeState.ALREADY_ACCEPTED:
        return <AlreadyAcceptedState />;
        
      case WelcomeState.VALID_TOKEN:
        return <ValidTokenState 
          form={form} 
          handleCreateAccount={handleCreateAccount} 
          isSubmitting={isSubmitting} 
        />;
        
      case WelcomeState.CREATING_ACCOUNT:
        return <CreatingAccountState />;
        
      case WelcomeState.ACCOUNT_CREATED:
        return <AccountCreatedState />;
        
      case WelcomeState.EDITING_LOCATION:
        return (
          <LocationEditingState invitation={invitation} />
        );
    }
  };

  return (
    <>
      {renderContent()}
    </>
  );
};

export default WelcomeController;
